/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ConsentData } from '../types/consent.types';
import { getCookieWrapper } from '@/shared/utils/storage/cookieWrapper';

/**
 * Cookie name for storing consent data.
 * This is an essential cookie, allowed without user consent.
 */
const CONSENT_COOKIE_NAME = 'cdu_consent_v1';

/**
 * Default expiration time for consent (1 year in milliseconds).
 */
const CONSENT_EXPIRATION_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Current version of CDU terms.
 * Increment this when CDU terms are updated to require new consent.
 */
const CDU_TERMS_VERSION = '1.0';

/**
 * Gets the consent data from cookie storage (or in-memory if consent not accepted).
 *
 * @returns {ConsentData | null} Consent data if valid and not expired, null otherwise
 */
export function getConsent(): ConsentData | null {
  try {
    const cookieWrapper = getCookieWrapper();
    const cookieValue = cookieWrapper.get(CONSENT_COOKIE_NAME);

    if (!cookieValue) {
      return null;
    }

    const consentData: ConsentData = JSON.parse(cookieValue);

    // Check if consent is expired (older than 1 year)
    const now = Date.now();
    const consentAge = now - consentData.timestamp;
    if (consentAge > CONSENT_EXPIRATION_MS) {
      return null;
    }

    return consentData;
  } catch (error) {
    // Invalid cookie data or JSON parse error
    return null;
  }
}

/**
 * Saves consent data to cookie storage (or in-memory if consent not yet accepted).
 * When consent is accepted, this will migrate to real cookies.
 * When consent is rejected, this stores in-memory for the session.
 *
 * @param {ConsentData} consentData - The consent data to save
 * @returns {Promise<void>} Promise that resolves when consent is saved
 */
export async function saveConsent(consentData: ConsentData): Promise<void> {
  const consentWithVersion: ConsentData = {
    ...consentData,
    timestamp: Date.now(),
    version: CDU_TERMS_VERSION,
  };

  const cookieValue = JSON.stringify(consentWithVersion);
  const expirationDate = new Date(Date.now() + CONSENT_EXPIRATION_MS);

  // Consent cookie is essential. Persist accepted consent in real cookies immediately.
  if (consentWithVersion.status === 'accepted') {
    document.cookie = `${encodeURIComponent(CONSENT_COOKIE_NAME)}=${encodeURIComponent(cookieValue)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
    return;
  }

  // For pending/rejected, keep in-memory only for the session.
  const cookieWrapper = getCookieWrapper();
  cookieWrapper.set(CONSENT_COOKIE_NAME, cookieValue, {
    expires: expirationDate,
    path: '/',
    sameSite: 'Lax',
  });
}

/**
 * Clears the consent cookie from both real cookies and in-memory storage.
 *
 * @returns {void}
 */
export function clearConsent(): void {
  const cookieWrapper = getCookieWrapper();
  cookieWrapper.delete(CONSENT_COOKIE_NAME, { path: '/' });

  // Also clear from real cookies directly (in case it exists there)
  document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Clears real localStorage, falling back to the original reference if wrapped.
 */
function clearLocalStorage(): void {
  const originalLocalStorage = (window as any).__originalLocalStorage || window.localStorage;
  try {
    originalLocalStorage.clear();
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}

/**
 * Clears real sessionStorage, falling back to the original reference if wrapped.
 */
function clearSessionStorage(): void {
  const originalSessionStorage = (window as any).__originalSessionStorage || window.sessionStorage;
  try {
    originalSessionStorage.clear();
  } catch (error) {
    console.warn('Failed to clear sessionStorage:', error);
  }
}

/**
 * Clears all browser cookies by setting each to an expired date across all path/domain variants.
 */
function clearAllCookies(): void {
  try {
    const cookies = document.cookie.split(';');
    for (const raw of cookies) {
      const cookie = raw.trim();
      if (!cookie) continue;

      const trimmedName = cookie.split('=')[0].trim();
      const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
      document.cookie = `${trimmedName}=; ${expired}; path=/;`;
      document.cookie = `${trimmedName}=; ${expired}; path=/; domain=${window.location.hostname};`;
      document.cookie = `${trimmedName}=; ${expired}; path=/; domain=.${window.location.hostname};`;
    }
  } catch (error) {
    console.warn('Failed to clear cookies:', error);
  }
}

/**
 * Clears all client-side PERSISTENT storage (real localStorage, real sessionStorage, and real cookies)
 * including the consent cookie itself and all essential storage keys.
 * This is called when user rejects CDU consent to ensure privacy compliance.
 * 
 * Important: This does NOT clear in-memory storage, which is allowed to persist
 * for the current page session (lost on reload) based on legitimate interest (LGPD Art. 7, IX).
 *
 * @returns {void}
 */
export function clearAllClientStorage(): void {
  clearLocalStorage();
  clearSessionStorage();
  clearAllCookies();
}
